import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from '@ibk/shared-translation';
import { Icon, Input, Loading } from '@ibk/shared-ui';
import { EmptyState } from '../components/EmptyState/EmptyState';
import { EstablishmentCard } from '../components/EstablishmentCard/EstablishmentCard';
import { useVoucherPartner } from '../hooks/useVoucherPartner';
import { useGetAcceptedLocationsQuery } from '../services/benefitsNetworkApi';
import styles from './NetworkPage.module.css';
export const NetworkPage = ({ 'data-testid': dataTestId, isOpen = false, onClose }) => {
    const { t } = useTranslation();
    const { partner, isLoading: isLoadingPartner } = useVoucherPartner();
    const [locationState, setLocationState] = useState({
        hasPermission: null,
        isLoading: true,
        error: null,
        coordinates: null,
    });
    const [foodSearchTerm, setFoodSearchTerm] = useState('');
    const [mealSearchTerm, setMealSearchTerm] = useState('');
    const [debouncedFoodSearch, setDebouncedFoodSearch] = useState('');
    const [debouncedMealSearch, setDebouncedMealSearch] = useState('');
    const [activeTab, setActiveTab] = useState('alimentacao');
    const [currentPage, setCurrentPage] = useState(1);
    const [allEstablishments, setAllEstablishments] = useState([]);
    const [hasMoreData, setHasMoreData] = useState(true);
    const currentProduct = activeTab === 'alimentacao' ? 'VA' : 'VR';
    const currentSearchTerm = activeTab === 'alimentacao' ? debouncedFoodSearch : debouncedMealSearch;
    const partnerId = partner?.id ?? '';
    useEffect(() => {
        const timer = setTimeout(() => {
            if (foodSearchTerm.length === 0 || foodSearchTerm.trim().length >= 3) {
                setDebouncedFoodSearch(foodSearchTerm);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [foodSearchTerm]);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (mealSearchTerm.length === 0 || mealSearchTerm.trim().length >= 3) {
                setDebouncedMealSearch(mealSearchTerm);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [mealSearchTerm]);
    const shouldFetchData = Boolean(locationState.coordinates && locationState.hasPermission && partnerId && !isLoadingPartner);
    const { data: apiResponse, isLoading: isLoadingEstablishments, error: apiError, isFetching, } = useGetAcceptedLocationsQuery({
        partnerId: partnerId,
        latitude: locationState.coordinates?.latitude ?? 0,
        longitude: locationState.coordinates?.longitude ?? 0,
        product: currentProduct,
        radius: 10,
        limit: 100,
        offset: currentPage,
        ...(currentSearchTerm.trim() && { companyName: currentSearchTerm.trim() }),
    }, {
        skip: !shouldFetchData,
        refetchOnMountOrArgChange: true,
    });
    useEffect(() => {
        if (apiResponse?.result?.result) {
            const newEstablishments = apiResponse.result.result;
            if (currentPage === 1) {
                setAllEstablishments(newEstablishments);
            }
            else {
                setAllEstablishments(prev => {
                    const existingIds = new Set(prev.map(e => e.documentNumber));
                    const uniqueNew = newEstablishments.filter(e => !existingIds.has(e.documentNumber));
                    if (uniqueNew.length === 0) {
                        return prev;
                    }
                    return [...prev, ...uniqueNew];
                });
            }
            setHasMoreData(newEstablishments.length === 100);
        }
    }, [apiResponse, currentPage]);
    useEffect(() => {
        setCurrentPage(1);
        setAllEstablishments([]);
        setHasMoreData(true);
    }, [currentSearchTerm, currentProduct]);
    const getUserCoordinates = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation not supported'));
                return;
            }
            let resolved = false;
            const manualTimeout = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    reject(new Error('Manual timeout'));
                }
            }, 10000);
            navigator.geolocation.getCurrentPosition(position => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(manualTimeout);
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                }
            }, error => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(manualTimeout);
                    reject(error);
                }
            }, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            });
        });
    };
    const checkLocationPermission = async () => {
        if (!navigator.geolocation) {
            setLocationState({
                hasPermission: false,
                isLoading: false,
                error: 'Geolocation is not supported by this browser.',
                coordinates: null,
            });
            return;
        }
        setLocationState(prev => ({
            ...prev,
            isLoading: true,
            error: null,
        }));
        try {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            const isGranted = permission.state === 'granted';
            if (isGranted) {
                try {
                    const coords = await getUserCoordinates();
                    setLocationState({
                        hasPermission: true,
                        isLoading: false,
                        error: null,
                        coordinates: coords,
                    });
                }
                catch (error) {
                    setLocationState({
                        hasPermission: true,
                        isLoading: false,
                        error: 'Error getting coordinates',
                        coordinates: null,
                    });
                }
            }
            else {
                setLocationState({
                    hasPermission: false,
                    isLoading: false,
                    error: null,
                    coordinates: null,
                });
            }
            permission.addEventListener('change', async () => {
                if (permission.state === 'granted') {
                    try {
                        const coords = await getUserCoordinates();
                        setLocationState(prev => ({
                            ...prev,
                            hasPermission: true,
                            coordinates: coords,
                        }));
                    }
                    catch (error) {
                        setLocationState(prev => ({
                            ...prev,
                            hasPermission: true,
                            error: 'Error getting coordinates',
                        }));
                    }
                }
                else {
                    setLocationState(prev => ({
                        ...prev,
                        hasPermission: false,
                        coordinates: null,
                    }));
                }
            });
        }
        catch (error) {
            setLocationState({
                hasPermission: false,
                isLoading: false,
                error: 'Error checking location permission',
                coordinates: null,
            });
        }
    };
    useEffect(() => {
        if (isOpen) {
            setCurrentPage(1);
            setAllEstablishments([]);
            setHasMoreData(true);
            setFoodSearchTerm('');
            setMealSearchTerm('');
            setDebouncedFoodSearch('');
            setDebouncedMealSearch('');
            setActiveTab('alimentacao');
            void checkLocationPermission();
        }
    }, [isOpen]);
    const handleRequestPermission = async () => {
        try {
            setLocationState(prev => ({
                ...prev,
                isLoading: true,
            }));
            if (!navigator.geolocation) {
                throw new Error('Geolocation not supported');
            }
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: false,
                    timeout: 15000,
                    maximumAge: 0,
                });
            });
            setLocationState({
                hasPermission: true,
                coordinates: {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                },
                isLoading: false,
                error: null,
            });
        }
        catch (error) {
            setLocationState(prev => ({
                ...prev,
                hasPermission: false,
                coordinates: null,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Erro ao obter localização',
            }));
        }
    };
    if (!isOpen) {
        return null;
    }
    const renderHeader = () => {
        return (_jsxs(Modal.Header, { className: styles.modalHeader, children: [_jsxs("div", { className: styles.modalTitleHeader, children: [_jsx("button", { type: 'button', onClick: onClose, className: styles.backButton, "data-testid": 'button-network-modal-back', children: _jsx(Icon, { name: 'actionBack' }) }), _jsx(Modal.Title, { className: styles.modalTitle, children: t('benefits.network.title') })] }), renderTabs(), renderSearchSection()] }));
    };
    const renderTabs = () => {
        const RestaurantIcon = ({ className }) => (_jsx("svg", { width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', className: className, children: _jsx("path", { d: 'M7.67939 0.40828L8.37206 4.00156C8.67991 5.89715 8.11231 7.28239 6.66925 8.15727L6.87127 14.5627C6.87127 14.9585 6.76545 15.2918 6.5538 15.5626C6.30367 15.8542 6.00544 16 5.6591 16H4.79327C4.44693 16 4.15832 15.8542 3.92743 15.5626C3.69654 15.2709 3.58109 14.9376 3.58109 14.5627L3.81198 8.15727C2.34968 7.26156 1.77246 5.88673 2.08031 4.03281L2.77298 0.40828C2.84994 0.116651 3.03273 -0.0187476 3.32134 0.00208306C3.59071 0.106236 3.70616 0.304127 3.66768 0.595756L2.97501 4.25153C2.72488 5.70967 3.22514 6.7512 4.47579 7.37612L4.73554 7.53235L4.50465 14.5939C4.50465 14.7189 4.54314 14.8231 4.6201 14.9064C4.65858 14.9689 4.7163 15.0001 4.79327 15.0001H5.6591C5.75531 15.0001 5.82265 14.9689 5.86113 14.9064C5.91885 14.8231 5.94772 14.7189 5.94772 14.5939L5.71683 7.53235L6.00544 7.37612C7.23685 6.77204 7.72749 5.70967 7.47736 4.18904L6.78469 0.595756C6.74621 0.304127 6.86165 0.106236 7.13103 0.00208306C7.41964 -0.0187476 7.60243 0.116651 7.67939 0.40828ZM12.9899 0.00208306C13.2785 0.00208306 13.519 0.116651 13.7114 0.345788C13.9038 0.574925 14 0.866554 14 1.22067V14.5627C14 14.9793 13.8846 15.323 13.6537 15.5938C13.4228 15.8646 13.1342 16 12.7878 16H12.0663C11.7392 16 11.4602 15.8646 11.2293 15.5938C10.9792 15.3022 10.8541 14.9376 10.8541 14.5002L11.085 9.53209C10.0075 8.1781 9.44955 6.93868 9.41106 5.81383C9.37258 4.68897 9.49765 3.80367 9.78626 3.15792C10.1711 2.24138 10.681 1.49147 11.3159 0.908215C11.9316 0.324958 12.4896 0.0229137 12.9899 0.00208306ZM13.0764 14.5627V1.22067C13.0572 1.07486 13.0283 1.00195 12.9899 1.00195C12.759 1.00195 12.403 1.22067 11.922 1.65812C11.4217 2.09556 10.9984 2.73089 10.6521 3.56412C10.3635 4.20987 10.2673 5.00143 10.3635 5.93881C10.4404 6.87619 10.9503 7.89689 11.8931 9.00091L12.0374 9.15714L11.7777 14.5314C11.7777 14.6981 11.8162 14.8231 11.8931 14.9064C11.9316 14.9689 11.9893 15.0001 12.0663 15.0001H12.7878C12.961 14.9793 13.0572 14.8335 13.0764 14.5627ZM4.27376 5.47012C4.00439 5.44929 3.86009 5.28265 3.84084 4.97019L4.07173 0.470772C4.12946 0.179143 4.293 0.0229137 4.56238 0.00208306C4.83175 0.0437443 4.97605 0.220805 4.99529 0.533264L4.7644 5.03268C4.72592 5.32431 4.572 5.48054 4.30262 5.50137C4.28338 5.48054 4.27376 5.47012 4.27376 5.47012ZM6.14974 5.50137C5.88037 5.48054 5.72645 5.32431 5.68796 5.03268L5.45707 0.533264C5.47632 0.220805 5.62062 0.0437443 5.88999 0.00208306C6.17861 0.0229137 6.33253 0.179143 6.35177 0.470772L6.58266 4.97019C6.6019 5.28265 6.46722 5.45971 6.17861 5.50137C6.15936 5.50137 6.14974 5.50137 6.14974 5.50137Z', fill: 'currentColor' }) }));
        const CartIcon = ({ className }) => (_jsx("svg", { width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', className: className, children: _jsx("path", { d: 'M0.448362 1H1.4852C2.07368 1 2.57808 1.35512 2.77424 1.87415H15.1042C15.6927 1.87415 16.113 2.42049 15.9729 2.96683L14.796 7.5561C14.5998 8.32098 13.8712 8.86732 13.0586 8.86732H4.25944L4.4556 9.93268C4.53967 10.3424 4.93199 10.6156 5.35233 10.6156H13.8992C14.1234 10.6156 14.3476 10.8341 14.3476 11.0527C14.3476 11.2985 14.1234 11.4898 13.8992 11.4898H5.35233C4.48362 11.4898 3.75503 10.9161 3.5869 10.0966L1.93356 2.22927C1.90554 2.03805 1.70938 1.87415 1.4852 1.87415H0.448362C0.196159 1.87415 0 1.68293 0 1.43707C0 1.21854 0.196159 1 0.448362 1ZM2.9704 2.74829L4.06328 7.99317H13.0586C13.4789 7.99317 13.8432 7.72 13.9273 7.33756L15.1042 2.74829H2.9704ZM4.7078 13.4566C4.7078 13.839 4.98803 14.1122 5.38035 14.1122C5.74464 14.1122 6.05289 13.839 6.05289 13.4566C6.05289 13.1015 5.74464 12.801 5.38035 12.801C4.98803 12.801 4.7078 13.1015 4.7078 13.4566ZM6.94962 13.4566C6.94962 14.0302 6.64137 14.522 6.16498 14.7951C5.66057 15.0683 5.0721 15.0683 4.59571 14.7951C4.09131 14.522 3.81108 14.0302 3.81108 13.4566C3.81108 12.9102 4.09131 12.4185 4.59571 12.1454C5.0721 11.8722 5.66057 11.8722 6.16498 12.1454C6.64137 12.4185 6.94962 12.9102 6.94962 13.4566ZM12.5541 12.801C12.1618 12.801 11.8816 13.1015 11.8816 13.4566C11.8816 13.839 12.1618 14.1122 12.5541 14.1122C12.9184 14.1122 13.2267 13.839 13.2267 13.4566C13.2267 13.1015 12.9184 12.801 12.5541 12.801ZM12.5541 14.9863C11.9657 14.9863 11.4613 14.7132 11.181 14.2215C10.9008 13.7571 10.9008 13.1834 11.181 12.6917C11.4613 12.2273 11.9657 11.9268 12.5541 11.9268C13.1146 11.9268 13.619 12.2273 13.8992 12.6917C14.1795 13.1834 14.1795 13.7571 13.8992 14.2215C13.619 14.7132 13.1146 14.9863 12.5541 14.9863Z', fill: 'currentColor' }) }));
        return (_jsxs("div", { className: styles.tabsContainer, children: [_jsxs("button", { className: `${styles.tabButton} ${activeTab === 'alimentacao' ? styles.tabButtonActive : ''}`, onClick: () => setActiveTab('alimentacao'), "data-testid": 'tab-alimentacao', children: [_jsx(RestaurantIcon, { className: `${styles.tabIcon} ${activeTab === 'alimentacao' ? styles.tabIconActive : styles.tabIconInactive}` }), t('benefits.network.tabs.food')] }), _jsxs("button", { className: `${styles.tabButton} ${activeTab === 'refeicao' ? styles.tabButtonActive : ''}`, onClick: () => setActiveTab('refeicao'), "data-testid": 'tab-refeicao', children: [_jsx(CartIcon, { className: `${styles.tabIcon} ${activeTab === 'refeicao' ? styles.tabIconActive : styles.tabIconInactive}` }), t('benefits.network.tabs.meal')] })] }));
    };
    const renderSearchSection = () => {
        const SEARCH_REGEX = /^[A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ0-9\s,.:+_<>?@#$()!%&*'/\\\-°ª]*$/;
        const handleSearchChange = (value) => {
            if (!SEARCH_REGEX.test(value)) {
                return;
            }
            if (activeTab === 'alimentacao') {
                setFoodSearchTerm(value);
            }
            else {
                setMealSearchTerm(value);
            }
        };
        return (_jsx("div", { className: styles.searchSection, children: _jsx("div", { className: styles.searchContainer, children: _jsx(Input, { placeholder: t('benefits.network.search.placeholder'), value: activeTab === 'alimentacao' ? foodSearchTerm : mealSearchTerm, onChange: e => handleSearchChange(e.target.value), dataTestId: 'network-search-input', size: 'small', leftElement: _jsx(Icon, { name: 'search', className: styles.searchIcon }) }) }) }));
    };
    const renderBody = () => {
        const hasSearchTerm = currentSearchTerm.length > 0;
        const hasApiError = Boolean(apiError);
        const isInitialLoading = isLoadingEstablishments && currentPage === 1;
        const isLoadingMore = isFetching && currentPage > 1;
        const isSearching = isFetching && currentPage === 1 && allEstablishments.length === 0;
        const hasNoResults = !isInitialLoading &&
            !isLoadingMore &&
            !isFetching &&
            !isLoadingEstablishments &&
            allEstablishments.length === 0 &&
            shouldFetchData;
        const handleScroll = (e) => {
            const target = e.currentTarget;
            const scrollPosition = target.scrollTop + target.clientHeight;
            const scrollHeight = target.scrollHeight;
            if (scrollHeight - scrollPosition < 100 && hasMoreData && !isFetching && !isLoadingEstablishments) {
                setCurrentPage(prev => prev + 1);
            }
        };
        return (_jsx(Modal.Body, { className: styles.modalBody, children: _jsxs("div", { className: styles.networkContent, onScroll: handleScroll, children: [isLoadingPartner && _jsx(EmptyState, { type: 'loading' }), !isLoadingPartner && !partnerId && _jsx(EmptyState, { type: 'api-error' }), !isLoadingPartner && partnerId && locationState.isLoading && _jsx(EmptyState, { type: 'loading' }), !isLoadingPartner && partnerId && !locationState.isLoading && locationState.hasPermission === false && (_jsx(EmptyState, { type: 'permission-denied', onRequestPermission: () => void handleRequestPermission() })), !isLoadingPartner &&
                        partnerId &&
                        !locationState.isLoading &&
                        locationState.hasPermission &&
                        (isInitialLoading || isSearching) && _jsx(EmptyState, { type: 'loading' }), !isLoadingPartner && partnerId && !isSearching && hasApiError && _jsx(EmptyState, { type: 'api-error' }), !isLoadingPartner && partnerId && !isSearching && !hasApiError && hasNoResults && !hasSearchTerm && (_jsx(EmptyState, { type: 'no-results-location' })), !isLoadingPartner && partnerId && !isSearching && !hasApiError && hasNoResults && hasSearchTerm && (_jsx(EmptyState, { type: 'no-results-search' })), !isLoadingPartner && partnerId && !isSearching && !hasApiError && allEstablishments.length > 0 && (_jsxs("div", { className: styles.establishmentsList, children: [allEstablishments.map((establishment, index) => (_jsx(EstablishmentCard, { establishment: establishment }, `${establishment.documentNumber}-${index}`))), isFetching && currentPage > 1 && (_jsxs("div", { className: styles.loadingMore, children: [_jsx(Loading, { size: 'small' }), _jsx("span", { children: t('benefits.network.loadingMore') })] }))] }))] }) }));
    };
    return (_jsxs(Modal, { show: isOpen, onHide: onClose, dialogClassName: styles.modalDialog, contentClassName: styles.modalContent, className: styles.modal, backdropClassName: styles.backdrop, "data-testid": dataTestId || 'modalNetworkContainer', children: [renderHeader(), renderBody()] }));
};
